import type { FC } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";

import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import type { ISortedColumn } from "app/@types/userdata";

import { getTableColumns } from "./PagesTableColumns";
import type { IPagesFilters } from "./PagesTableTop";
import PagesTableTop from "./PagesTableTop";
import { PagesTableWrapper, StyledTableTitle } from "./styledComponents";

interface ICommonTableProps extends IPagesFilters {
    key: string;
}

export interface IDomainObj {
    icon?: string;
    name?: string;
}

interface IPagesTableProps {
    params: ICommonTableProps;
    downloadExcelPermitted: boolean;
    isDisplayedBanner?: boolean;
}

const PAGES_V2_API_ENDPOINT_PREFIX = "widgetApi/PopularPages/PopularPages_v2";

export const PagesTable: FC<IPagesTableProps> = (props) => {
    const [selectedDomainObj, setSelectedDomainObj] = useState<IDomainObj | object>({});
    const { params, downloadExcelPermitted, isDisplayedBanner } = props;
    const DEFAULT_SORTED_COLUMN: ISortedColumn = { field: "Share", sortDirection: "desc" };
    const PAGES_TABLE_API_ENDPOINT = `${PAGES_V2_API_ENDPOINT_PREFIX}/Table`;
    const PAGES_EXCEL_API_ENDPOINT = `/${PAGES_V2_API_ENDPOINT_PREFIX}/Excel`;
    const { from, to, timeGranularity = "Monthly", webSource = "Total", isWindow, country, key } = params;

    const keysArr = key?.split(",");
    const isCompare = keysArr.length > 1;
    const domainsArr = isCompare ? keysArr : [key];

    const initialFilters = {
        country,
        includeSubDomains: false,
        webSource,
        timeGranularity,
        to,
        from,
        isWindow,
        keys: domainsArr[0],
        pageSize: 100,
        page: 1,
        isAll: true,
        sort: DEFAULT_SORTED_COLUMN?.field,
        asc: DEFAULT_SORTED_COLUMN?.sortDirection === "asc",
    };

    const transformData = (data) => {
        return {
            ...data,
            records: data.Data.map((record) => {
                return {
                    ...record,
                };
            }),
        };
    };

    const noDataTrackingCallback = (numOfRecommendations) => {
        allTrackers.dispatchTrackEvent("no data", null, "Subdomain recommendation", {
            category: "no data",
            recommendation: numOfRecommendations,
        });
    };

    const NoDateComponentToRender = (
        <TableNoData
            noDataCallback={() => noDataTrackingCallback(0)}
            messageTitle={translate("global.nodata.notavilable")}
        />
    );

    const tableTitle: string | undefined = proNavigator.current().viewConfig?.title;

    const tableColumns = getTableColumns(DEFAULT_SORTED_COLUMN);
    return (
        <PagesTableWrapper
            className="pages-table"
            addMarginTop={isDisplayedBanner}
        >
            {tableTitle ? (
                <StyledTableTitle
                    fontVariant="h5"
                    color={colorsPalettes.carbon[500]}
                >
                    {i18nFilter()(tableTitle)}
                </StyledTableTitle>
            ) : null}
            <SWReactTableWrapper
                transformData={transformData}
                serverApi={PAGES_TABLE_API_ENDPOINT}
                initialFilters={initialFilters}
                tableColumns={tableColumns}
                tableOptions={{
                    noDataComponent: NoDateComponentToRender,
                    tableSelectionTrackingParam: "Domain",
                    trackName: "Traffic Sources",
                    metric: "PopularPagesTableMetric",
                }}
                recordsField="records"
                totalRecordsField="TotalCount"
                pageIndent={1}
            >
                {(topComponentProps) => (
                    <PagesTableTop
                        {...topComponentProps}
                        setSelectedDomainObj={setSelectedDomainObj}
                        selectedDomainObj={selectedDomainObj}
                        searchValue={""}
                        initialFilters={initialFilters}
                        downloadExcelPermitted={downloadExcelPermitted}
                        excelLinkResource={PAGES_EXCEL_API_ENDPOINT}
                        showDomainSelector={isCompare}
                        domains={domainsArr}
                    />
                )}
            </SWReactTableWrapper>
        </PagesTableWrapper>
    );
};
